import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SendMessage from './modal/send_message-test.modal';
import Icon from '../forms/icon.component';
import style from './templates-wa.module.scss';
import { useLocation } from 'react-router-dom';
import { useDarkMode } from '../../hooks/use-dark-mode.hook';

interface Props {
  data?: any;
  payload?: any;
  vars?: any;
  exmplesCampaigns?: any;
}

const MockupIphone = React.forwardRef<HTMLDivElement, Props>(
  ({ payload, vars, exmplesCampaigns }, ref) => {
    const { t } = useTranslation();
    const [showAllOptions, setShowAllOptions] = useState(false);
    const isDarkTheme = useDarkMode();

    const validPaths = [
      '/sendCampaigns/flows-campaigns',
      '/sendCampaigns/fallback-campaigns',
      '/sendCampaigns/standard-campaigns'
    ];
    const location = useLocation();
    const isValidRoute = validPaths.includes(location.pathname);
    const routeFallback = location.pathname === '/sendCampaigns/fallback-campaigns';

    const [showModal, setShowModal] = useState({
      show: false,
      data: { typeModal: 'sms' }
    });
    const handleShowModal = async () => {
      setShowModal({ show: false, data: { typeModal: 'sms' } });
    };
    const replaceVariablesWithContext = (
      text: string,
      section: string,
      vars: any,
      examples: any[]
    ): string => {
      if (!text || !examples || !vars || !vars[section]) return text;

      return text.replace(/{{([^{}]+)}}/g, (_, variableKey) => {
        const columnHeader = vars[section]?.[`{{${variableKey}}}`];
        if (!columnHeader) return `{{${variableKey}}}`;
        const found = examples.find((item) => item.header === columnHeader);
        return found?.value ?? `{{${variableKey}}}`;
      });
    };

    const replaceButtonVariables = (
      text: string,
      btnIndex: number,
      vars: { BUTTONS?: Record<string, string> },
      examples: { header: string; value: string }[]
    ): string => {
      if (!text || !vars.BUTTONS) return text;
      const headerKey = vars.BUTTONS[btnIndex];
      if (!headerKey) return text;
      const found = examples.find((item) => item.header === headerKey);
      if (!found) return text;
      return text.replace(/{{[^{}]+}}/g, found.value);
    };

    const renderButtonsPreview = () => {
      const buttonsComponent = Array.isArray(payload?.components)
        ? payload.components.find((component: any) => component.type === 'BUTTONS')
        : undefined;
      const allButtons = buttonsComponent?.buttons || [];
      const visibleButtons = allButtons.slice(0, 3);
      const totalButtons = allButtons.length;

      return (
        <>
          {visibleButtons.map((btn: any, btnIndex: number) => (
            <div key={btnIndex} className="me-1">
              <hr className="my-0" />
              <div
                className={`${isDarkTheme ? style.preview_template_dark_btn : style.preview_template_btn} ${isDarkTheme ? style.preview_template_dark : style.preview_template}`}
                style={{ boxShadow: 'none' }}
              >
                {btn.type === 'URL' && (
                  <>
                    <i className="bi-box-arrow-up-right"></i>{' '}
                    {btn.example
                      ? replaceButtonVariables(btn.text, btnIndex, vars, exmplesCampaigns)
                      : btn.text}
                  </>
                )}
                {btn.type === 'QUICK_REPLY' && (
                  <>
                    <i className="bi-reply"></i> {btn.text}
                  </>
                )}
                {btn.type === 'QUICK_REPLY_DISABLE_MKT' && (
                  <>
                    <i className="bi-reply"></i>
                    {btn.text}
                  </>
                )}
                {btn.type === 'PHONE_NUMBER' && (
                  <>
                    <i className="bi-telephone"></i>
                    {btn.text}
                  </>
                )}
                {btn.type === 'COPY_CODE' && (
                  <>
                    <i className="bi-clipboard"></i>
                    {btn.text}
                  </>
                )}
              </div>
            </div>
          ))}

          {totalButtons > 3 && (
            <div className="cursor-pointer" onClick={() => setShowAllOptions(true)}>
              <hr className="my-0" />
              <div
                className={`${isDarkTheme ? style.preview_template_dark_btn : style.preview_template_btn} ${isDarkTheme ? style.preview_template_dark : style.preview_template}`}
                style={{ boxShadow: 'none' }}
              >
                <i className="bi-list me-2"></i>
                {t('templates.wa.create.see-all-options')}
              </div>
            </div>
          )}
        </>
      );
    };

    function parseBold(text: string): string {
      return text.replace(/\*(.*?)\*/g, '<strong>$1</strong>');
    }

    function parseStrikethrough(text: string): string {
      return text.replace(/~(.*?)~/g, '<del>$1</del>');
    }

    function parseWhatsAppFormatting(text: string): string {
      let formatted = text;
      formatted = parseBold(formatted);
      formatted = parseStrikethrough(formatted);
      formatted = formatted.replace(/\n/g, '<br/>');
      return formatted;
    }

    return (
      <>
        <div ref={ref} className={isDarkTheme ? 'wrap-iphone-wa-dark me-20' : 'wrap-iphone-wa'}>
          <div className={style.bg_whatsApp}>
            <div className={style.container_message}>
              <div className={style.sticky}>
                <div className="mt-3 me-2">
                  <div
                    className={isDarkTheme ? style.preview_template_dark : style.preview_template}
                  >
                    <div style={{ padding: '16px 16px 5px 16px' }}>
                      {(payload?.components ?? []).map((component: any, index: number) => {
                        if (component.type === 'HEADER') {
                          if (component.format === 'TEXT') {
                            const parsed = replaceVariablesWithContext(
                              component.text,
                              'HEADER',
                              vars,
                              exmplesCampaigns
                            );
                            return (
                              <div
                                key={index}
                                className={
                                  isDarkTheme
                                    ? style.preview_template_dark_header
                                    : style.preview_template_header
                                }
                              >
                                {parsed}
                              </div>
                            );
                          }
                          if (component.format === 'IMAGE') {
                            return (
                              <div
                                key={index}
                                className={
                                  isDarkTheme
                                    ? style.preview_template_dark_header
                                    : style.preview_template_header
                                }
                              >
                                <div
                                  className={
                                    isDarkTheme
                                      ? style.preview_template_dark_image
                                      : style.preview_template_image
                                  }
                                  style={{
                                    backgroundImage: `url(${component.example?.header_handle?.[0] ?? ''})`
                                  }}
                                >
                                  {!component.example?.header_handle?.[0] && (
                                    <i className="bi-card-image"></i>
                                  )}
                                </div>
                              </div>
                            );
                          }
                          if (component.format === 'VIDEO') {
                            return (
                              <div
                                key={index}
                                className={
                                  isDarkTheme
                                    ? style.preview_template_dark_header
                                    : style.preview_template_header
                                }
                              >
                                <div
                                  className={
                                    isDarkTheme
                                      ? style.preview_template_dark_video
                                      : style.preview_template_video
                                  }
                                >
                                  <i className="bi-play-circle-fill"></i>
                                </div>
                              </div>
                            );
                          }
                          if (component.format === 'DOCUMENT') {
                            return (
                              <div
                                key={index}
                                className={
                                  isDarkTheme
                                    ? style.preview_template_dark_header
                                    : style.preview_template_header
                                }
                              >
                                <div
                                  className={
                                    isDarkTheme
                                      ? style.preview_template_dark_video
                                      : style.preview_template_video
                                  }
                                >
                                  <i className="bi-filetype-pdf"></i>
                                </div>
                              </div>
                            );
                          }
                          if (component.format === 'LOCATION') {
                            return (
                              <div
                                key={index}
                                className={
                                  isDarkTheme
                                    ? style.preview_template_dark_header
                                    : style.preview_template_header
                                }
                              >
                                <div
                                  className={
                                    isDarkTheme
                                      ? style.preview_template_dark_video
                                      : style.preview_template_video
                                  }
                                >
                                  <i className="bi-geo-alt"></i>
                                </div>
                              </div>
                            );
                          }
                        }

                        if (component.type === 'BODY') {
                          const parsed = replaceVariablesWithContext(
                            component.text,
                            'BODY',
                            vars,
                            exmplesCampaigns
                          );
                          return (
                            <div
                              key={index}
                              dangerouslySetInnerHTML={{
                                __html: parseWhatsAppFormatting(parsed)
                              }}
                            />
                          );
                        }

                        if (component.type === 'FOOTER') {
                          const parsed = replaceVariablesWithContext(
                            component.text,
                            'FOOTER',
                            vars,
                            exmplesCampaigns
                          );
                          return (
                            <div
                              key={index}
                              className={
                                isDarkTheme
                                  ? style.preview_template_dark_footer
                                  : style.preview_template_footer
                              }
                            >
                              {parsed}
                            </div>
                          );
                        }

                        return null;
                      })}
                    </div>

                    {renderButtonsPreview()}
                  </div>
                </div>
              </div>
            </div>

            {showAllOptions && (
              <div
                className={
                  isDarkTheme ? style.preview_template_dark_options : style.preview_template_options
                }
              >
                <div className="d-flex align-items-center my-2">
                  <div onClick={() => setShowAllOptions(false)}>
                    <i className="bi-x fs-2x ms-3 cursor-pointer"></i>
                  </div>
                  <div className="d-flex w-100 text-center justify-content-center align-items-center">
                    <span className="me-5 text-center">Todas las opciones</span>
                  </div>
                </div>

                {(payload?.components.find((c: any) => c.type === 'BUTTONS')?.buttons ?? []).map(
                  (btn: any, index: number) => (
                    <div key={index}>
                      <div
                        className={
                          (isDarkTheme
                            ? style.preview_template_dark_btn
                            : style.preview_template_btn) +
                          ' text-start ' +
                          (isDarkTheme ? style.preview_template_dark : style.preview_template)
                        }
                        style={{ boxShadow: 'none' }}
                      >
                        {btn.type === 'URL' && (
                          <>
                            <i className="bi-box-arrow-up-right"></i>{' '}
                            {btn.example
                              ? replaceButtonVariables(btn.text, index, vars, exmplesCampaigns)
                              : btn.text}
                          </>
                        )}
                        {btn.type === 'QUICK_REPLY' && (
                          <>
                            <i className="bi-reply"></i> {btn.text}
                          </>
                        )}
                        {btn.type === 'QUICK_REPLY_DISABLE_MKT' && (
                          <>
                            <i className="bi-reply"></i> {btn.text}
                          </>
                        )}
                        {btn.type === 'PHONE_NUMBER' && (
                          <>
                            <i className="bi-telephone"></i> {btn.text}
                          </>
                        )}
                        {btn.type === 'COPY_CODE' && (
                          <>
                            <i className="bi-clipboard"></i> {btn.text}
                          </>
                        )}
                      </div>
                    </div>
                  )
                )}
              </div>
            )}
          </div>
        </div>
        {/* {isValidRoute && (
          <div className={`d-flex justify-content-center mt-5 ${routeFallback ? 'ms-15' : 'me-3'}`}>
            <button
              className="btn btn-info fw-bolder"
              type="button"
              onClick={() => setShowModal({ show: true, data: { typeModal: 'sms' } })}
            >
              <Icon icon="ki-send" size="fs-1" />
              <span className="ms-2"> Enviar Mensaje de prueba</span>
            </button>
            <SendMessage
              show={showModal.show}
              onRequestClose={handleShowModal}
              typeMockup={'whatsapp'}
            />
          </div>
        )} */}
      </>
    );
  }
);

export default MockupIphone;
